import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { GraphVizNodeComponentType } from '@/modules/graphViz/graphVizNodeConfig';
import { COLORS } from '@/styles/tokens/colors';

interface DisplayAsOption {
  label: string;
  value: GraphVizNodeComponentType;
}

// Menu order matches the Figma design (Icon above Tile).
const DISPLAY_AS_OPTIONS: DisplayAsOption[] = [
  { label: 'Icon', value: GraphVizNodeComponentType.TileIcon },
  { label: 'Tile', value: GraphVizNodeComponentType.Tile },
];

const ICON_SIZE = 16;

function getTypeLabel(type: GraphVizNodeComponentType): string {
  return type === GraphVizNodeComponentType.TileIcon ? 'Icon' : 'Tile';
}

interface DisplayAsPickerProps {
  currentType: GraphVizNodeComponentType;
  /** When true, the selection mixes Tile and Icon tiles — no checkmark shown. */
  isMixed?: boolean;
  onTypeChange: (type: GraphVizNodeComponentType) => void;
  disabled?: boolean;
}

/**
 * Toolbar dropdown for converting the selected tiles between the "Tile" and
 * "Icon" display types in bulk. Mirrors the IconShapePicker / TileEmphasisPicker
 * pattern; the trigger shows the current type as text (or "Mixed" when the
 * selection spans both types).
 */
export function DisplayAsPicker({
  currentType,
  isMixed = false,
  onTypeChange,
  disabled = false,
}: DisplayAsPickerProps) {
  const showCheck = !isMixed;
  const triggerLabel = isMixed ? 'Mixed' : getTypeLabel(currentType);

  return (
    <ButtonWithPopover
      variant="transparent"
      size="xs"
      disabled={disabled}
      label={triggerLabel}
      highlightWhenOpen
      popperProps={{
        // Anchor the menu to the bottom-left of the trigger so it opens
        // downward and extends to the right, matching the design.
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        transformOrigin: { horizontal: 'left', vertical: 'top' },
        PaperProps: { sx: { minWidth: 160 } },
      }}
    >
      <SelectItemGroupLabel label="Display as" />
      {DISPLAY_AS_OPTIONS.map(({ label, value }) => {
        const isActive = showCheck && value === currentType;
        return (
          <MenuItem
            key={value}
            label={label}
            iconAfter={
              isActive ? (
                <CheckIcon size={ICON_SIZE} color={COLORS.TEAL[600]} />
              ) : undefined
            }
            onClick={() => onTypeChange(value)}
          />
        );
      })}
    </ButtonWithPopover>
  );
}
